/**
 * blocks/gutenberg/adwerba/carousel/frontend.js
 *
 * @author jjarolim
 */

/**
 *  
 * Carousel-Funktionalität basierend auf Embla 
 * ======================================================
 * 
 * Nötiges HTML-Markup:
 * --------------------
 * <div class="carousel">
 *     <nav class="carousel-navigation-previous" /> // Optional
 *     <div class="carousel-viewport">
 *         <div class="carousel-container">
 *             <div class="carousel-slide"></div>
 *             <div class="carousel-slide"></div>
 *             ...
 *         </div>
 *     </div>
 *     <nav class="carousel-navigation-next" /> // Optional
 *     <nav class="carousel-pagination" /> // Optional
 * </div>
 * 
 * 
 * Optionen:
 * ---------
 * Optionen werden mittels data-Attributen auf dem .carousel-Element definiert.
 * (z.B. <div class="carousel" data-carousel-slides-per-view="2">...)
 *
 * - data-carousel-slide-spacing: Abstand zwischen den Slides
 *   Typ: string
 *   Default: 1rem
 * 
 * - data-carousel-slides-per-view: Anzahl der Slides prViewport
 *   Typ: number
 *   Default: 1
 * 
 * - data-carousel-start-index: Index des Start-Slides
 *   Typ: number
 *   Default: 0
 * 
 * - data-carousel-autoplay: Autoplay-Optionen
 *   Typ: boolean | object
 *   Default: false
 *   Objekt-Attribute: siehe https://www.embla-carousel.com/plugins/autoplay/#options
 *  
 * - data-carousel-autoplay-delay: Autoplay-Delay
 *   Typ: number
 *   Default: 8000
 * 
 * - data-carousel-auto-height: Automatische Höhe prSlide
 *   Typ: boolean
 *   Default: false
 * 
 * - data-carousel-fade: Fade-Animation statt Slide-Animation
 *   Typ: boolean
 *   Default: false
 * 
 * - data-carousel-loop: Endlosschleife
 *   Typ: boolean
 *   Default: false
 * 
 * - data-carousel-no-swiping: Swipe-Verhalten ausschalten
 *   Typ: boolean
 *   Default: false
 * 
 * - data-carousel-align: Horizontale Ausrichtung der Slides
 *   Typ: string
 *   Default: "start"
 *   Mögliche Werte: "start", "center", "end"
 *
 * - data-carousel-breakpoints: Breakpoint-spezifische Optionen
 *   Typ: object
 *   Default: {}
 * 
 * 
 * Objekt-Attribute: Breakpoint-Größen in Pixel als Schlüssel, Objekt mit Optionen als Wert
 * 
 * Beispiel:
 * 
 *    data-carousel-loop="true"
 *    data-carousel-autoplay="true"
 *    data-carousel-breakpoints='{
 *       "768": { "slides-per-view": 2, "loop": false, "autoplay": false },
 *       "1024": { "slides-per-view": 3 }
 *    }'
 * 
 *   
 * API:
 * ----
 * Für erweiterte Funktionalitäten kann mit der Embla-API interagiert werden.
 * Die Dokumentation der API ist unter https://www.embla-com/api/ zu finden.
 * Um zur API zu gelangen muss ein Event 'carousel:requestApi' auf dem .carousel-Element ausgelöst werden.
 * Dies löst wiederum ein Event 'carousel:getApi' aus, welches die Embla-API als Argument übergibt.
 * 
 * Um auch sicherstellen zu können, dass die Carousel-Infrastruktur steht, wird das 
 * Event 'carousel:initialized' ausgelöst, sobald alle Carousels initialisiert wurden.
 *  
 * Beispiel:
 * 
 * const initializeMyCarouselBlock = () => {
 * 
 *     // Prepare to receive the emblaApi
 * 
 *     $( '.my-carousel-block .carousel' ).on( 'carousel:getApi', function( event, emblaApi ) {
 * 
 *         // Here, we have the emblaApi availableand can interact with it:
 * 
 *         emblaApi.scrollTo(1);
 * 
 *     } )
 * 
 *     // Request the emblaApi
 * 
 *     $('.my-carousel-block .carousel').trigger( 'carousel:requestApi' );
 * 
 * }
 * 
 * // Initialize your carousel block as soon the infrastructure is ready
 * 
 * $( window ).on( 'carousel:initialized', initializeMyCarouselBlock );
 * 
 * @author gbuttinger, jjarolim
 */

import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import AutoHeight from "embla-carousel-auto-height";
import Fade from "embla-carousel-fade";
import { toggleKeyboardFocusability } from "./wcag/focus-utilities";

const $ = window.jQuery // Use global WP jQuery instance

const initCarousel = $carouselElement => {

    // Setup styles
    setupStyles($carouselElement);

    // Inititalize the carousel
    const emblaApi = EmblaCarousel(
        $carouselElement.find(".carousel-viewport")[0],
        setupOptions($carouselElement),
        setupPlugins($carouselElement)
    );

    // Setup Navigation & Pagination
    setupNavigation($carouselElement, emblaApi);
    setupPagination($carouselElement, emblaApi);

    // Setup events request API
    
    $carouselElement.on( "carousel:requestApi", function () {
        $carouselElement.trigger( "carousel:getApi", emblaApi );
    });

    // Reinitialize the carousel on resize
    window.addEventListener("resize", () => {
        setupStyles($carouselElement);
        emblaApi.reInit(
            setupOptions($carouselElement),
            setupPlugins($carouselElement)
        );
    });

	// AutoHeight doesn't detect, if the height of a slide changes,
	// so we need to reInit the carousel, when the height of a slide changes.
    const resizeObserver = new ResizeObserver(entries => {
        emblaApi.reInit(
            setupOptions($carouselElement),
            setupPlugins($carouselElement)
        );
    });
    $('> .carousel-viewport > .carousel-container > .carousel-slide', $carouselElement).each(function() {
        const slideElement = this;
        resizeObserver.observe(slideElement);
    });

    // SetupWCAG
    setupWcag($carouselElement, emblaApi);

    // Setup Timerbar
    setupTimerbar($carouselElement, emblaApi);

    // Setup Autoplay Toggle (Pause/Play)
    setupAutoplayToggle($carouselElement, emblaApi);

}


const getBreakpointProperty = ( property, $carouselElement ) => {
    let value = $carouselElement.data( `carousel-${property}` );
    const breakpointOptions = $carouselElement.data(`carousel-breakpoints`);
    if ( breakpointOptions ) {
        for ( const [ breakpoint, breakpointProps ] of Object.entries(
            breakpointOptions
        )) {
            if (
                property in breakpointProps &&
                window.matchMedia( `(min-width: ${breakpoint}px)` ).matches
            ) {
                value = breakpointProps[property];
            }
        }
    }
    return value;
}


const setupStyles = $carouselElement => {

    // Slide spacing is handled via the CSS variable --slide-spacing

    const slideSpacing = getBreakpointProperty( "slide-spacing", $carouselElement ) ?? "1rem";

    $carouselElement.css("--slide-spacing", slideSpacing);

    // Slide per view are handled via the CSS variable --slide-size

    const slidesPerView = getBreakpointProperty("slides-per-view", $carouselElement) ?? 1;

    $carouselElement.css("--slide-size", `${100 / slidesPerView}%`);
}


const setupAutoplayToggle = ($carouselElement, emblaApi) => {

    const $toggleWrapper = $carouselElement.find('.carousel-autoplay-toggle').eq(0);
    const $button = $toggleWrapper.find('.carousel-autoplay-toggle-button').eq(0);

    if (!$button.length) {
        return;
    }

    const plugins = typeof emblaApi.plugins === 'function' ? emblaApi.plugins() : null;
    const autoplayPlugin = plugins && plugins.autoplay;

    if (!autoplayPlugin || typeof autoplayPlugin.play !== 'function' || typeof autoplayPlugin.stop !== 'function') {
        $toggleWrapper.hide();
        return;
    }

    let isPaused = false;

    const updateState = () => {
        $button.attr('aria-pressed', isPaused ? 'true' : 'false');
        $button.attr('aria-label', isPaused ? 'Wiedergabe starten' : 'Wiedergabe pausieren');
        $carouselElement.toggleClass('is-autoplay-paused', isPaused);
        $carouselElement.toggleClass('is-timerbar-paused', isPaused);
    };

    updateState();

    $button.on('click', event => {
        event.preventDefault();

        isPaused = !isPaused;

        if (isPaused) {
            autoplayPlugin.stop();
        } else {
            autoplayPlugin.play();
        }

        updateState();
    });
}


const setupTimerbar = ($carouselElement, emblaApi) => {

    const $timerbar = $carouselElement.find('.carousel-timerbar').eq(0);

    if (!$timerbar.length) {
        return;
    }

    const scrollSnaps = emblaApi.scrollSnapList() || [];
    if (scrollSnaps.length <= 1) {
        $timerbar.hide();
        return;
    }

    const autoplay = getBreakpointProperty('autoplay', $carouselElement);
    if (!(autoplay === true || typeof autoplay === 'object')) {
        $timerbar.hide();
        return;
    }

    let autoplayDelay = getBreakpointProperty( 'autoplay-delay', $carouselElement);

    if (!autoplayDelay) {
        autoplayDelay = 8000;
    }

    const plugins = typeof emblaApi.plugins === 'function' ? emblaApi.plugins() : null;
    const autoplayPlugin = plugins && plugins.autoplay;

    $carouselElement.css('--carousel-timerbar-duration', `${autoplayDelay}ms`);

    const restartTimerbar = (reason) => {
        $carouselElement.removeClass('is-timerbar-animating');
        // force reflow to restart CSS animation
        // eslint-disable-next-line no-unused-expressions
        $timerbar[0] && $timerbar[0].offsetWidth;
        $carouselElement.addClass('is-timerbar-animating');
    };

    $timerbar.on('animationstart', () => {});
    $timerbar.on('animationend', () => {});

    emblaApi.on('init', () => {
        restartTimerbar('embla:init');
    });

    emblaApi.on('select', () => {
        restartTimerbar('embla:select');
    });

    emblaApi.on('reInit', () => {
        restartTimerbar('embla:reInit');
    });

    emblaApi.on('pointerDown', () => {
        $carouselElement.addClass('is-timerbar-paused');
    });

    const resumeTimerbar = (source) => {
        const pluginsLocal = typeof emblaApi.plugins === 'function' ? emblaApi.plugins() : null;
        const autoplayPluginLocal = pluginsLocal && pluginsLocal.autoplay;

        $carouselElement.removeClass('is-timerbar-paused');
        // Wichtig: kein explizites autoplayPluginLocal.play() aufrufen,
        // damit das Embla-Autoplay-Timing nicht doppelt gestartet wird.
    };

    emblaApi.on('pointerUp', () => resumeTimerbar('pointerUp'));
    emblaApi.on('settle', () => resumeTimerbar('settle'));
}


const setupPlugins = $carouselElement => {

    const plugins = [];

    // Autoplay
    const autoplay = getBreakpointProperty(
        "autoplay",
        $carouselElement
    );

    if (autoplay === true || typeof autoplay === "object") {

        let autoplayDelay = getBreakpointProperty(
            "autoplay-delay",
            $carouselElement
        );

        // Default: 8 seconds
    
        if ( ! autoplayDelay ) {
            autoplayDelay = 8000;
        }

        const autoplayOptions = autoplay === true
            ? {
                delay: autoplayDelay,
                stopOnInteraction: false,
                stopOnMouseEnter: false,
                stopOnFocusIn: false,
                defaultInteraction: false,
            }
            : {
                delay: autoplayDelay,
                stopOnInteraction: false,
                stopOnMouseEnter: false,
                stopOnFocusIn: false,
                defaultInteraction: false,
                ...autoplay,
            };

        plugins.push(
            Autoplay(autoplayOptions)
        );
    }

    // Autheight

    if (getBreakpointProperty("auto-height", $carouselElement) === true) {
        plugins.push(AutoHeight());
    }

    // Autheight
    if (getBreakpointProperty("fade", $carouselElement) === true) {
        plugins.push(Fade());
    }

    return plugins;
}


const setupOptions = $carouselElement => {
    const startIndex = getBreakpointProperty(
        "start-index",
        $carouselElement
    );
    const loop = getBreakpointProperty("loop", $carouselElement);
    const align =
        getBreakpointProperty("align", $carouselElement) ?? "start";
    return {
        align: align,
        ...(startIndex ? { startIndex: startIndex } : {}),
        ...(loop ? { loop: loop } : {}),
        ...(
            getBreakpointProperty("no-swiping", $carouselElement)
                ? { watchDrag: false }
                : {}
        ),
    };
}


const setupNavigation = ($carouselElement, emblaApi) => {

    const prevButton = $(`
        <button
            class="carousel-navigation-prev"
            aria-label="Previous slide"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512">
                <path fill-rule="evenodd" d="m372.1 490 10.2-10.4-221.1-219c-2.6-2.6-2.6-6.9 0-9.4l221.1-219-10.2-10.4c-2.6-2.3-6.9-2.3-9.2 0l-232 229.4c-2.3 2.6-2.3 6.8 0 9.4l232 229.4c2.3 2.7 6.5 2.7 9.2 0z" clip-rule="evenodd" />
            </svg>
        </button>
    `);

    prevButton.on("click", () => {
        emblaApi.scrollPrev();
    });

    const nextButton = $(`
        <button
            class="carousel-navigation-next"
            aria-label="Next slide"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512">
                <path fill-rule="evenodd" d="m139.3 22-10.2 10.4 221.1 219c2.6 2.6 2.6 6.9 0 9.4l-221.1 219 10.2 10.4c2.6 2.3 6.9 2.3 9.2 0l232-229.4c2.3-2.6 2.3-6.8 0-9.4L148.5 22c-2.3-2.7-6.6-2.7-9.2 0z" clip-rule="evenodd" />
            </svg>
        </button>
    `);

    nextButton.on("click", () => {
        emblaApi.scrollNext();
    });

    const navPrevious = $carouselElement.find(".carousel-navigation-previous").eq(0);
    const navNext = $carouselElement.find(".carousel-navigation-next").eq(0);

    if (navPrevious) {
        navPrevious.append(prevButton);
    }

    if (navNext) {
        navNext.append(nextButton);
    }

    if (navPrevious || navNext) {

        // Update navigation buttons on embla change events.

        function updateNavButtons() {
            prevButton.prop("disabled", !emblaApi.canScrollPrev());
            nextButton.prop("disabled", !emblaApi.canScrollNext());
        }

        emblaApi.on("init", updateNavButtons);
        emblaApi.on("reInit", updateNavButtons);
        emblaApi.on("select", updateNavButtons);

    }

}


const setupPagination = ($carouselElement, emblaApi) => {

    const paginationElement = $carouselElement
        .find(".carousel-pagination")
        .eq(0);

    if (paginationElement) {

        const scrollSnaps = emblaApi.scrollSnapList() || [];

        function createPagination() {

            paginationElement.empty();

            scrollSnaps.forEach((snap, index) => {

                const button = $("<button/>");
                button.on("click", () => emblaApi.scrollTo(index));

                function setActiveClass() {
                    if (emblaApi.selectedScrollSnap() === index) {
                        button.addClass("active");
                    } else {
                        button.removeClass("active");
                    }
                }

                setActiveClass();
                emblaApi.on("select", () => setActiveClass());
                emblaApi.on("init", () => setActiveClass());
                emblaApi.on("reInit", () => setActiveClass());

                paginationElement.append(button);
            });
        }

        emblaApi.on("init", createPagination);
        emblaApi.on("reInit", createPagination);
    }
}


const setupWcag = ($carouselElement, emblaApi) => {

    // Initialize TabIndexes in the beginning:
    // No slide (and slide child elements) except the first one should be focusable

    $('.carousel-slide', $carouselElement).each(function (index) {

        // Disable all Slides except the first one

        if (index > 0) {
            toggleSlideFocusability($carouselElement, index);
        }

    });


    $carouselElement.data('slideIndex', 0);

    emblaApi.on('select', () => {

        // Update tabIndex to prevent keyboard navigation to non-active slides

        const currentSlideIndex = $carouselElement.data('slideIndex');
        const nextSlideIndex = emblaApi.selectedScrollSnap();
        const slideCount = emblaApi.scrollSnapList().length;

        toggleSlideFocusability($carouselElement, currentSlideIndex);
        toggleSlideFocusability($carouselElement, nextSlideIndex);

        $carouselElement.data('slideIndex', nextSlideIndex);

        // Fokus neu setzen, falls erster oder letzter Slide erreicht wurde

        if (currentSlideIndex === 0) {
            $('.carousel-navigation-next', $carouselElement).get(0)?.focus();
        } else if (currentSlideIndex === slideCount - 1) {
            $('.carousel-navigation-previous', $carouselElement).get(0)?.focus();
        }

    })
}


/**
 * WCAG: Make sure that the carousel can be navigated with the keyboard.
 *       We set the tabindex on all interaction elements on non-visible slides to -1
 *       to prevent that the user navigates to a non-visible slide.
 * 
 * @param {*} $carouselElement 
 * @param {*} slideIndex 
 */
const toggleSlideFocusability = ($carouselElement, slideIndex) => {

    const selector = '.carousel-slide:nth-child( ' + (slideIndex + 1) + ' )';
    const $slide = $(selector, $carouselElement);

    toggleKeyboardFocusability($slide);

}


const initCarousels = () => {
    $( '.carousel' ).each( function () {
        initCarousel( $( this ) );
    } )
    $( window ).trigger( 'carousel:initialized' )
}


const init = () => {
    // On Document Ready
    $( function() {
        initCarousels()
    } )
}


export { init };